import { useTranslation } from "react-i18next";
import { CanvasArtwork } from "../data/canvasArtworks";
import { canvasCollectionCopy } from "../data/canvasArtworkCatalog";
import { digitalCollectionCopy } from "../data/digitalArtworkCatalog";
import { digitalArtworkById } from "../data/digitalArtworks";
import {
  formatCanvasDimensions,
  formatInvestmentPrice,
  resolveCanvasImageUrl,
} from "../lib/canvasArtworkHelpers";
import { ArtWork } from "../types/ArtWork";

export function useCanvasArtwork(work: CanvasArtwork) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("he") ? "he" : "en";
  const prefix = `canvas.works.${work.id}`;
  const collectionLabel = canvasCollectionCopy[work.collection][lang];
  const title = t(`${prefix}.name`);
  return {
    ...work,
    title,
    name: `${collectionLabel} — ${title}`,
    description: t(`${prefix}.description`),
    medium: t(`${prefix}.medium`),
    collectionLabel,
    dimensionsFormatted: formatCanvasDimensions(work.dimensions, lang),
    investmentPrice: formatInvestmentPrice(work.investmentPriceUsd, lang),
    galleryImages: work.images.map((image) => ({
      url: resolveCanvasImageUrl(image),
      alt: title,
      label: image.role
        ? t(`canvas.imageRoles.${image.role}`, { defaultValue: image.role })
        : undefined,
    })),
  };
}

export function useDigitalArtwork(work: ArtWork) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("he") ? "he" : "en";
  const digital = digitalArtworkById(work.id);
  const collection = digital?.collection ?? "studio";
  const collectionLabel = digitalCollectionCopy[collection][lang];
  const prefix = `digital.works.${work.id}`;
  const title = t(`${prefix}.name`, { defaultValue: work.name });
  const description = t(`${prefix}.description`, {
    defaultValue: work.description,
  });
  return {
    ...work,
    imageUrl: digital?.imageUrl ?? work.imageUrl,
    collection,
    collectionLabel,
    title,
    name: `${collectionLabel} — ${title}`,
    description,
  };
}

export function useLinkCaption(label: string, caption?: string) {
  const { t } = useTranslation();
  if (caption) {
    return t(`links.captions.${label}`, { defaultValue: caption });
  }
  return t(`links.labels.${label}`, { defaultValue: label });
}
